import { Component, OnDestroy, OnInit } from '@angular/core';
import { UntypedFormBuilder, UntypedFormGroup } from '@angular/forms';
import { Subscription } from 'rxjs';
import { DataService } from 'src/app/services/data.service';
import {
  getDefaultIncludeTagNames,
  PrizeTagFilterStateService,
  TagFilterState,
} from '../services/prize-tag-filter-state.service';

interface TagOption {
  _id: string;
  name: string;
}

/**
 * "Tags" filter section — include/exclude tag rule builder, using the same `p-multiSelect`
 * chip-picker pattern as the CMS's other tag pickers (e.g. Item's "Select Tags" field), sourced
 * from the `tags` entity. Rules are the single real source of truth behind
 * PrizeTagFilterStateService's state$: every change is published there immediately, which is
 * what GeneratePrizesPanelComponent's "must have a Tag filter" submit guard reads from.
 *
 * "Exclude any other tag" is a dynamic rule flag (excludeAllOtherTags), not an enumerated exclude
 * list — per the ticket, the default is "item must ONLY have the Prize tag", which should hold
 * regardless of what other tags exist/get added later. Unchecking it switches to an explicit,
 * user-editable exclude-tag list instead.
 */
@Component({
  selector: 'app-prize-tag-filter',
  templateUrl: './prize-tag-filter.component.html',
  styleUrls: ['./prize-tag-filter.component.sass'],
})
export class PrizeTagFilterComponent implements OnInit, OnDestroy {
  tagOptions: TagOption[] = [];
  form: UntypedFormGroup;

  private formValueSub: Subscription;

  constructor(
    private fb: UntypedFormBuilder,
    private dataService: DataService,
    private tagFilterState: PrizeTagFilterStateService,
  ) {}

  async ngOnInit(): Promise<void> {
    const initialState = this.tagFilterState.getState();
    this.form = this.fb.group({
      includeTagRefs: [initialState.includeTagRefs],
      excludeAllOtherTags: [initialState.excludeAllOtherTags],
      excludeTagRefs: [initialState.excludeTagRefs],
    });
    this.publishState();
    this.formValueSub = this.form.valueChanges.subscribe(() => this.publishState());

    const tags = await this.dataService.getAllOfTypeAsync('tags', {
      query: {},
      autopopulate: false,
      virtuals: false,
      sort: { name: 1 },
    });
    this.tagOptions = (tags || []).map((tag: any) => ({ _id: tag._id, name: tag.name }));

    // Re-check pristineness against the form's *current* value (not a snapshot taken before this
    // await) — the user may have already edited the form during the tags fetch.
    if (this.isFormStillPristineDefault()) {
      this.resolveDefaultIncludeTags();
    }
  }

  ngOnDestroy(): void {
    this.formValueSub?.unsubscribe();
  }

  get excludeAllOtherTagsControl() {
    return this.form.get('excludeAllOtherTags')!;
  }

  private isFormStillPristineDefault(): boolean {
    const value = this.form.value;
    return value.includeTagRefs.length === 0 && value.excludeAllOtherTags === true && value.excludeTagRefs.length === 0;
  }

  private resolveDefaultIncludeTags(): void {
    const defaultNames = getDefaultIncludeTagNames().map((name) => this.normalizeName(name));
    const matches = this.tagOptions.filter((option) => defaultNames.includes(this.normalizeName(option.name)));
    if (matches.length > 0) {
      this.form.get('includeTagRefs')!.setValue(matches.map((option) => option._id));
    }
  }

  private normalizeName(value: string): string {
    return value.trim().toLowerCase();
  }

  private publishState(): void {
    const state: TagFilterState = this.form.value;
    this.tagFilterState.setState(state);
  }
}
